import type { ApiResponse } from './types';

/**
 * El servidor autentica por cookie de sesión (CookieSessionWriter). El cliente nunca guarda el
 * token: alcanza con pedir que las credenciales viajen y con leer el 401 cuando la sesión murió.
 */
const BASE_URL = import.meta.env['VITE_API_URL'] ?? '';

/**
 * Lo que se muestra cuando el servidor rechazó y no dijo por qué.
 *
 * Existe una sola vez y en un solo lugar: un mensaje vacío tiene que producir siempre la misma
 * frase, y no una distinta según qué pantalla lo haya pedido.
 */
export const GENERIC_FAILURE_MESSAGE = 'La solicitud falló.';

/** Descarta vacíos y espacios en blanco. Un mensaje en blanco es lo mismo que ninguno. */
function usableMessages(candidates: readonly (string | null | undefined)[]): string[] {
  return candidates
    .map((candidate) => (typeof candidate === 'string' ? candidate.trim() : ''))
    .filter((candidate) => candidate.length > 0);
}

export class ApiError extends Error {
  readonly messages: string[];

  constructor(
    readonly statusCode: number,
    messages: readonly (string | null | undefined)[],
  ) {
    const usable = usableMessages(messages);
    super(usable[0] ?? GENERIC_FAILURE_MESSAGE);
    this.messages = usable.length > 0 ? usable : [GENERIC_FAILURE_MESSAGE];
    this.name = 'ApiError';
  }

  /** La sesión venció o nunca existió: hay que volver a la pantalla de ingreso. */
  get isUnauthenticated(): boolean {
    return this.statusCode === 401;
  }

  /**
   * Hay sesión, pero este pedido no está permitido.
   *
   * Se distingue del 401 porque el remedio es otro: volver a ingresar no cambia nada, así que la
   * pantalla tiene que mostrar el motivo en vez de mandar a la persona a la puerta.
   */
  get isForbidden(): boolean {
    return this.statusCode === 403;
  }
}

/** El navegador no llegó al servidor. Distinta de un 500: esto se puede reintentar más tarde. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('No se pudo contactar al servidor.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined>;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`;
  if (!query) {
    return url;
  }

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) {
      params.set(key, String(value));
    }
  }

  const serialized = params.toString();
  return serialized.length > 0 ? `${url}?${serialized}` : url;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, query } = options;

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: body instanceof FormData ? {} : { 'Content-Type': 'application/json' },
    signal: signal ?? null,
  };

  if (body !== undefined) {
    init.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), init);
  } catch (cause) {
    // AbortError no es una falla de red: es una cancelación pedida por la propia pantalla.
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw cause;
    }
    throw new NetworkError(cause);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let envelope: ApiResponse<T> | null = null;

  if (text.length > 0) {
    try {
      envelope = JSON.parse(text) as ApiResponse<T>;
    } catch {
      /*
       * El cuerpo no es JSON, y eso no lo vuelve inservible.
       *
       * Acá se descartaba el texto y se ponía «El servidor devolvió una respuesta inesperada»,
       * que le contaba a la persona algo sobre el formato de la respuesta y nada sobre lo que le
       * pasó. Los rechazos de autenticación son justamente los que llegan así: el middleware
       * corta antes de que exista el sobre `Response<T>` y escribe texto pelado —«Token is not on
       * the whitelist»—. Ese texto ES el motivo, y es lo único que hay.
       *
       * Cuando el pedido salió bien pero el cuerpo no es JSON, ahí sí es un problema de formato y
       * se dice como tal.
       */
      if (response.ok) {
        throw new ApiError(response.status, ['El servidor devolvió una respuesta inesperada.']);
      }

      throw new ApiError(response.status, [text]);
    }
  }

  if (!response.ok || envelope?.isSuccess === false) {
    // Un cuerpo vacío, un `message` vacío o uno lleno de cadenas en blanco terminan todos en la
    // misma frase: `ApiError` se encarga, así que acá no hace falta preguntarlo tres veces.
    throw new ApiError(response.status, envelope?.message ?? []);
  }

  return (envelope?.data ?? (undefined as T)) as T;
}

export const http = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
